// The onboarding AI-match sync gate, shared by every surface that can lead a
// user to a property: the Feed banner, the Property Selector, the MLS Active
// Listings table and My Inventory.
//
// One query key means one answer — if each surface asked separately they would
// drift, and a selector that lets you pick a property the Feed says is not
// ready is exactly the dead end this exists to prevent.
//
// Polls only while a sync is actually open, and stops the moment it completes;
// for the overwhelming majority of users (anyone not newly onboarded) the
// first response is `syncing: false` and nothing polls at all.
import { useQuery } from "@tanstack/react-query";
import { getAiSyncStatus } from "@/services/v2/matches";
import { USER_INFO_KEY } from "@/constants/common";

export const AI_SYNC_QUERY_KEY = ["aiSyncStatus"];

const OPEN = { syncing: false, released: true, total: 0, settled: 0 };

// Read directly rather than through helpers/common: this hook mounts on the
// public feed, and that module drags in most of the app.
const isSignedIn = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_INFO_KEY))?.userInfo?.userId != null;
  } catch {
    return false;
  }
};

const useAiSyncStatus = ({ enabled = true } = {}) => {
  // The gate answers for the CURRENT user, so signed out there is nothing to
  // ask: the public feed was firing this on every anonymous load for a 401.
  const signedIn = isSignedIn();
  const { data } = useQuery({
    queryKey: AI_SYNC_QUERY_KEY,
    enabled: enabled && signedIn,
    queryFn: async () => {
      const response = await getAiSyncStatus();
      return response?.data || OPEN;
    },
    // Fail open: a user must never be locked out of their own properties
    // because this call failed.
    retry: 1,
    staleTime: 5000,
    refetchInterval: (query) =>
      query.state.data?.syncing ? 5000 : false,
  });

  const status = data || OPEN;
  return {
    // Hold properties back entirely: the full-screen "we're building your
    // matches" state.
    isGated: status.syncing === true && status.released === false,
    // Still finishing in the background, but properties are usable.
    isSyncing: status.syncing === true,
    settled: status.settled ?? 0,
    total: status.total ?? 0,
  };
};

export default useAiSyncStatus;
